import { useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Modal, Button } from '@symphenia/ui-components';
import {
  checkPayrollMonth,
  finalizePayrollMonth,
  type MonatsBefund,
  type MonatsPruefung,
  type PayrollMonth,
} from '../api/payroll.api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  jahr: number;
  monat: number;
  /** „Testmonteur Peter, August 2026" — steht im Titel und in der Frage. */
  bezeichnung: string;
  onFertig: (monat: PayrollMonth) => void;
}

const TEXT: Record<MonatsBefund['code'], string> = {
  ALREADY_FINAL: 'Der Monat ist bereits fertiggestellt.',
  NO_WORK_SCHEDULE: 'Für diesen Monat gibt es kein Arbeitszeitmodell — ohne Soll lässt sich nicht rechnen.',
  EMPTY: 'In diesem Monat ist nichts erfasst.',
  DAYS_WITHOUT_ENTRY: 'Arbeitstage ohne Eintrag',
  INCOMPLETE_TIMES: 'Kommt ohne Geht (oder umgekehrt) — diese Tage rechnen mit 0 Stunden',
  SCHEDULE_CHANGE: 'Im Monat beginnt ein weiteres Arbeitszeitmodell — gerechnet wird mit dem ersten',
};

function tage(dates: string[] | undefined): string {
  if (!dates?.length) return '';
  return dates.map((d) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`).join(' · ');
}

function Zeile({ befund, art }: { befund: MonatsBefund; art: 'verhindert' | 'hinweis' }) {
  const Icon = art === 'verhindert' ? AlertCircle : AlertTriangle;
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon
        size={16}
        className={`mt-0.5 flex-shrink-0 ${art === 'verhindert' ? 'text-destructive' : 'text-amber-600'}`}
        aria-hidden="true"
      />
      <span className="text-foreground">
        {TEXT[befund.code]}
        {befund.dates?.length ? (
          <span className="block text-xs tabular-nums text-muted-foreground">{tage(befund.dates)}</span>
        ) : null}
      </span>
    </li>
  );
}

/**
 * Lohnmonat fertigstellen (AP-22 Teil 2, G1) — eine **Handlung** (AP-36 Fall 3),
 * deshalb ein Knopf mit dem Verb.
 *
 * Der Dialog zeigt zuerst, was der Server geprüft hat: was verhindert und
 * worauf er hinweist. Unvollständige Tage sind ein Hinweis, kein Hindernis.
 */
export function FertigstellenDialog({ isOpen, onClose, userId, jahr, monat, bezeichnung, onFertig }: Props) {
  const [pruefung, setPruefung] = useState<MonatsPruefung | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPruefung(null);
    setError(null);
    checkPayrollMonth(userId, jahr, monat)
      .then(setPruefung)
      .catch(() => setError('Die Prüfung konnte nicht geladen werden.'));
  }, [isOpen, userId, jahr, monat]);

  const fertigstellen = async () => {
    setLaeuft(true);
    setError(null);
    try {
      onFertig(await finalizePayrollMonth(userId, jahr, monat));
    } catch (err) {
      const antwort = (err as { response?: { data?: { code?: string; verhindert?: MonatsBefund[] } } })?.response?.data;
      // Der Stand hat sich seit der Prüfung geändert — die neue Liste zeigen.
      if (antwort?.verhindert) setPruefung({ verhindert: antwort.verhindert, hinweise: [] });
      setError(
        antwort?.code === 'PAYROLL_MONTH_FINAL'
          ? 'Der Monat ist inzwischen fertiggestellt worden.'
          : 'Der Monat konnte nicht fertiggestellt werden.',
      );
    } finally {
      setLaeuft(false);
    }
  };

  const verhindert = (pruefung?.verhindert.length ?? 0) > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Fertigstellen — ${bezeichnung}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button onClick={() => void fertigstellen()} disabled={laeuft || !pruefung || verhindert}>
            {laeuft ? 'Wird fertiggestellt…' : 'Fertigstellen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!pruefung && !error && <p className="text-sm text-muted-foreground">Der Monat wird geprüft…</p>}

        {pruefung && verhindert && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">So lässt sich der Monat nicht fertigstellen:</p>
            <ul className="space-y-1.5">
              {pruefung.verhindert.map((b) => (
                <Zeile key={b.code} befund={b} art="verhindert" />
              ))}
            </ul>
          </div>
        )}

        {pruefung && !verhindert && pruefung.hinweise.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Bitte vorher ansehen:</p>
            <ul className="space-y-1.5">
              {pruefung.hinweise.map((b) => (
                <Zeile key={b.code} befund={b} art="hinweis" />
              ))}
            </ul>
          </div>
        )}

        {pruefung && !verhindert && pruefung.hinweise.length === 0 && (
          <p className="flex items-start gap-2 text-sm text-foreground">
            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" />
            Der Monat ist vollständig erfasst.
          </p>
        )}

        {pruefung && !verhindert && (
          <p className="text-sm text-muted-foreground">
            Mit dem Fertigstellen werden die Zahlen des Monats festgehalten. Danach lässt er sich exportieren und
            nicht mehr ändern.
          </p>
        )}

        {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
      </div>
    </Modal>
  );
}
